<template>
  <svg
    class="avatar"
    :class="[`is-${expression}`, { [`surprise-${surprise}`]: surprise }]"
    viewBox="0 0 128 128"
    :style="style"
    :role="label ? 'img' : undefined"
    :aria-label="label || undefined"
    :aria-hidden="label ? undefined : 'true'"
  >
    <circle
      v-if="expression === 'listening'"
      class="ring"
      cx="64"
      cy="64"
      r="62"
    />

    <g v-if="surprise === 'doze'">
      <g class="z">
        <path transform="translate(84 30) scale(.6)" d="M0 0 h7 l-7 8 h7" />
      </g>
      <g class="z">
        <path transform="translate(91 22) scale(.75)" d="M0 0 h7 l-7 8 h7" />
      </g>
      <g class="z">
        <path transform="translate(98 14) scale(.9)" d="M0 0 h7 l-7 8 h7" />
      </g>
    </g>

    <g class="body">
      <!-- Coffin lying down, rounded corners through stroke-linejoin -->
      <polygon
        class="coffin"
        points="26,55 46,41 102,51 102,77 46,87 26,73"
        stroke-width="18"
        stroke-linejoin="round"
      />

      <g
        v-if="expression === 'idle' || expression === 'listening'"
        class="eyes"
      >
        <ellipse class="eye eye-l" cx="52" cy="63" rx="7" ry="10" />
        <ellipse class="eye eye-r" cx="80" cy="63" rx="7" ry="10" />
      </g>

      <template v-else-if="expression === 'thinking'">
        <g class="eyes">
          <ellipse class="eye-fill" cx="52" cy="62" rx="6.5" ry="8" />
          <ellipse class="eye-fill" cx="80" cy="62" rx="6.5" ry="8" />
        </g>
        <circle class="dot" cx="57" cy="80" r="3" />
        <circle class="dot" cx="66" cy="80" r="3" />
        <circle class="dot" cx="75" cy="80" r="3" />
      </template>

      <template v-else-if="expression === 'happy'">
        <path class="smile-eye" d="M45 66 Q52 54 59 66" />
        <path class="smile-eye" d="M73 66 Q80 54 87 66" />
      </template>
    </g>
  </svg>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import {
  ALL_SURPRISES,
  SURPRISE_DURATIONS,
  allowedSurprises,
  isSurpriseAllowed,
  pickSurprise,
  surpriseDelay,
  type AvatarSurprise
} from '@/views/layouts/main-layout/ai-assistant/avatarSurprises'

export type AvatarExpression = 'idle' | 'listening' | 'thinking' | 'happy'

export default defineComponent({
  name: 'AiAssistantAvatar',
  props: {
    expression: {
      type: String as PropType<AvatarExpression>,
      required: false,
      default: 'idle'
    },
    // In px, or any CSS length ('3rem', '100%')
    size: {
      type: [Number, String],
      required: false,
      default: 56
    },
    // Background and eyes colour, the theme's primary colour by default
    bg: {
      type: String,
      required: false,
      default: null
    },
    // Coffin colour, the theme's contrast colour by default
    face: {
      type: String,
      required: false,
      default: null
    },
    // Screen reader text, leave empty when the avatar is decorative
    label: {
      type: String,
      required: false,
      default: ''
    },
    // Random little surprises while idle
    surprises: {
      type: Boolean,
      required: false,
      default: true
    },
    // Seconds between two surprises
    surpriseMinDelay: {
      type: Number,
      required: false,
      default: 15
    },
    surpriseMaxDelay: {
      type: Number,
      required: false,
      default: 40
    },
    surpriseAnimations: {
      type: Array as PropType<AvatarSurprise[]>,
      required: false,
      default: () => [...ALL_SURPRISES]
    },
    // False keeps only the sober surprises (SERIOUS_SURPRISES)
    playful: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data() {
    return {
      surprise: null as AvatarSurprise | null,
      lastSurprise: null as AvatarSurprise | null,
      nextTimer: undefined as ReturnType<typeof setTimeout> | undefined,
      endTimer: undefined as ReturnType<typeof setTimeout> | undefined
    }
  },
  computed: {
    style() {
      const size = typeof this.size === 'number' ? `${this.size}px` : this.size
      return {
        width: size,
        height: size,
        ...(this.bg ? { '--avatar-bg': this.bg } : {}),
        ...(this.face ? { '--avatar-face': this.face } : {})
      }
    },
    allowedSurprises(): AvatarSurprise[] {
      return allowedSurprises(this.surpriseAnimations, this.playful)
    },
    // Any change restarts the wait, so a route switching to serious applies
    // to the very next surprise
    surpriseSettings(): string {
      return [
        this.expression,
        this.surprises,
        this.surpriseMinDelay,
        this.surpriseMaxDelay,
        this.allowedSurprises.join()
      ].join('|')
    }
  },
  watch: {
    surpriseSettings() {
      this.clearTimers()
      this.surprise = null
      this.schedule()
    }
  },
  mounted() {
    this.schedule()
  },
  beforeUnmount() {
    this.clearTimers()
  },
  methods: {
    clearTimers() {
      clearTimeout(this.nextTimer)
      clearTimeout(this.endTimer)
    },
    schedule() {
      clearTimeout(this.nextTimer)
      if (
        !this.surprises ||
        this.expression !== 'idle' ||
        !this.allowedSurprises.length ||
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      ) {
        return
      }
      this.nextTimer = setTimeout(
        () => {
          // No point animating a background tab
          if (document.hidden) {
            this.schedule()
            return
          }
          const surprise = pickSurprise(
            this.allowedSurprises,
            this.lastSurprise
          )
          if (surprise) {
            this.play(surprise)
          }
        },
        surpriseDelay(this.surpriseMinDelay, this.surpriseMaxDelay)
      )
    },
    // Plays a surprise right away (on hover, on click…)
    play(surprise: AvatarSurprise) {
      if (
        this.expression !== 'idle' ||
        !isSurpriseAllowed(surprise, this.playful)
      ) {
        return
      }
      this.clearTimers()
      this.surprise = null
      // Next frame, so the same surprise played twice restarts its animation
      requestAnimationFrame(() => {
        this.surprise = surprise
        this.lastSurprise = surprise
        this.endTimer = setTimeout(() => {
          this.surprise = null
          this.schedule()
        }, SURPRISE_DURATIONS[surprise])
      })
    }
  }
})
</script>

<style scoped>
.avatar {
  --_bg: var(--avatar-bg, var(--color-primary-500));
  --_face: var(--avatar-face, var(--color-contrast-primary-500, #ffffff));
  display: inline-block;
  border-radius: 50%;
  corner-shape: squircle;
  background: var(--_bg);
  flex-shrink: 0;
  overflow: visible;
}

.coffin {
  fill: var(--_face);
  stroke: var(--_face);
}
.eye,
.eye-fill,
.dot {
  fill: var(--_bg);
}
.eye {
  transform-box: fill-box;
  transform-origin: center;
}
.smile-eye {
  fill: none;
  stroke: var(--_bg);
  stroke-width: 5.5;
  stroke-linecap: round;
}
.ring {
  fill: none;
  stroke: var(--_face);
  stroke-width: 3;
  opacity: 0;
  transform-origin: 64px 64px;
  animation: ring 2.4s ease-out infinite;
}
.body {
  transform-origin: 64px 64px;
}

/* Expressions */

.is-idle .body {
  animation: bob 4s ease-in-out infinite;
}
.is-idle .eyes {
  animation: look 7s ease-in-out infinite;
}
.is-idle .eye {
  animation: blink 5s infinite;
}

.is-listening .body {
  animation: tilt 3s ease-in-out infinite;
}
.is-listening .eye {
  animation: attend 3s ease-in-out infinite;
}

.is-thinking .body {
  animation: bob 4s ease-in-out infinite;
}
.is-thinking .eyes {
  animation: ponder 4s ease-in-out infinite;
}
.is-thinking .dot {
  opacity: 0.25;
  animation: dot 1.2s ease-in-out infinite;
}
.is-thinking .dot:nth-of-type(2) {
  animation-delay: 0.2s;
}
.is-thinking .dot:nth-of-type(3) {
  animation-delay: 0.4s;
}

.is-happy .body {
  transform-origin: 64px 90px;
  animation: hop 2.2s ease-in-out infinite;
}

/* Surprises, only while idle */

.is-idle.surprise-wink .body {
  animation: wink-tilt 1.2s ease-in-out;
}
.is-idle.surprise-wink .eyes,
.is-idle.surprise-wink .eye-l {
  animation: none;
}
.is-idle.surprise-wink .eye-r {
  animation: wink 1.2s ease-in-out;
}

.is-idle.surprise-startle .body {
  animation: startle 1.4s ease-out;
}
.is-idle.surprise-startle .eyes {
  animation: none;
}
.is-idle.surprise-startle .eye {
  animation: startle-eyes 1.4s ease-out;
}

.is-idle.surprise-look-around .eyes {
  animation: look-around 2s ease-in-out;
}

.is-idle.surprise-shiver .body {
  animation: shiver 1s linear;
}

.is-idle.surprise-roll .body {
  animation: roll 1.4s cubic-bezier(0.6, 0, 0.4, 1);
}
.is-idle.surprise-roll .eyes {
  animation: none;
}

.is-idle.surprise-doze .body {
  animation: doze-body 3.6s ease-in-out;
}
.is-idle.surprise-doze .eyes {
  animation: none;
}
.is-idle.surprise-doze .eye {
  animation: doze-eyes 3.6s ease-in-out;
}
.z path {
  fill: none;
  stroke: var(--_face);
  stroke-width: 2.4;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.z {
  opacity: 0;
  animation: z-float 2.4s ease-out 0.6s both;
}
.z:nth-of-type(2) {
  animation-delay: 0.9s;
}
.z:nth-of-type(3) {
  animation-delay: 1.2s;
}

@keyframes bob {
  0%,
  100% {
    transform: translateY(0) rotate(0);
  }
  50% {
    transform: translateY(-2px) rotate(-1.5deg);
  }
}
@keyframes blink {
  0%,
  92%,
  100% {
    transform: scaleY(1);
  }
  95% {
    transform: scaleY(0.12);
  }
}
@keyframes look {
  0%,
  40%,
  100% {
    transform: translateX(0);
  }
  48%,
  62% {
    transform: translateX(4px);
  }
  70%,
  82% {
    transform: translateX(-3px);
  }
}
@keyframes tilt {
  0%,
  100% {
    transform: rotate(-4deg);
  }
  50% {
    transform: rotate(-6deg) translateY(-1px);
  }
}
@keyframes attend {
  0%,
  88%,
  100% {
    transform: scale(1.12);
  }
  93% {
    transform: scale(1.12, 0.15);
  }
}
@keyframes ring {
  0% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  100% {
    transform: scale(1);
    opacity: 0;
  }
}
@keyframes ponder {
  0%,
  100% {
    transform: translate(3px, -4px);
  }
  50% {
    transform: translate(-2px, -4px);
  }
}
@keyframes dot {
  0%,
  100% {
    opacity: 0.25;
  }
  40% {
    opacity: 1;
  }
}
@keyframes hop {
  0%,
  60%,
  100% {
    transform: translateY(0) scale(1, 1);
  }
  70% {
    transform: translateY(0) scale(1.04, 0.96);
  }
  80% {
    transform: translateY(-5px) scale(0.98, 1.02);
  }
  90% {
    transform: translateY(0) scale(1.02, 0.98);
  }
}
@keyframes wink {
  0%,
  100% {
    transform: scaleY(1);
  }
  20%,
  60% {
    transform: scaleY(0.1);
  }
}
@keyframes wink-tilt {
  0%,
  100% {
    transform: rotate(0);
  }
  20%,
  60% {
    transform: rotate(-5deg);
  }
}
@keyframes startle-eyes {
  0%,
  100% {
    transform: scale(1);
  }
  12%,
  60% {
    transform: scale(1.35);
  }
}
@keyframes startle {
  0%,
  100% {
    transform: translateY(0);
  }
  12% {
    transform: translateY(-5px);
  }
  30% {
    transform: translateY(0);
  }
}
@keyframes look-around {
  0%,
  100% {
    transform: translate(0, 0);
  }
  15%,
  30% {
    transform: translate(-5px, 0);
  }
  45%,
  60% {
    transform: translate(5px, 0);
  }
  75%,
  88% {
    transform: translate(0, -4px);
  }
}
@keyframes shiver {
  0%,
  100% {
    transform: translateX(0) rotate(0);
  }
  10%,
  30%,
  50%,
  70% {
    transform: translateX(-1.5px) rotate(-1deg);
  }
  20%,
  40%,
  60%,
  80% {
    transform: translateX(1.5px) rotate(1deg);
  }
  90% {
    transform: translateX(0) rotate(0);
  }
}
@keyframes roll {
  0% {
    transform: rotate(0);
  }
  100% {
    transform: rotate(360deg);
  }
}
@keyframes doze-eyes {
  0%,
  100% {
    transform: scaleY(1);
  }
  15%,
  85% {
    transform: scaleY(0.1);
  }
}
@keyframes doze-body {
  0%,
  100% {
    transform: rotate(0) translateY(0);
  }
  20%,
  80% {
    transform: rotate(4deg) translateY(2px);
  }
}
@keyframes z-float {
  0% {
    opacity: 0;
    transform: translateY(4px);
  }
  30% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translateY(-6px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .avatar,
  .avatar * {
    animation: none !important;
  }
  .is-thinking .dot {
    opacity: 1;
  }
  .is-thinking .eyes {
    transform: translate(0, -4px);
  }
}
</style>
